import { SAMPLE_ROUTES } from './sample-routes';

/**
 * Cifras de «WAIIS S.L.», la empresa ficticia del dashboard de ejemplo
 * (`/demo`) y de «Ver con datos de ejemplo».
 *
 * Antes cada pantalla tenía las suyas y no casaban entre sí (Inicio decía 228
 * viajes al mes, Uso 1284 y Viajes 128; Uso tenía más activos que vinculados).
 * Ahora todas salen de aquí, de UNA plantilla coherente, y siguen el filtro de
 * sede y de periodo igual que lo harían los datos de verdad:
 *
 *   350 en plantilla ≥ 238 registrados ≥ 217 vinculados ≥ 152 con rutina
 *   ≥ 118 con algún viaje ≥ 74 con viaje compartido ≥ 41 recurrentes
 *
 * Base: toda la empresa en los últimos 30 días. Los trayectos miden lo que
 * miden las rutas de ejemplo del mapa (~11 km de media).
 */

/** Plantilla declarada de «WAIIS S.L.» (la que dice la barra del ejemplo). */
export const SAMPLE_DECLARED_EMPLOYEES = 350;

/** Reparto de la plantilla entre las dos sedes de `assets/demo/offices.json`. */
const OFFICE_SHARE: Record<string, number> = {
  'demo-office-madrid': 0.62,
  'demo-office-getafe': 0.38,
};

/** Activos por días de periodo (hoy, semana, 30 días, trimestre, año): crece cada vez más despacio. */
const ACTIVE_BY_DAYS: [number, number][] = [
  [1, 41],
  [7, 98],
  [30, 156],
  [90, 178],
  [365, 201],
];

const BASE = {
  registered: 238,
  linked: 217,
  withRoutine: 152,
  tripCompleted: 118,
  sharedTrip: 74,
  recurrent: 41,
  /** Trayectos compartidos en 30 días. */
  sharedTrips: 228,
  /** Km de media de un trayecto compartido. */
  kmPerTrip: 11,
  passengersPerTrip: 1.6,
  /** kg de CO₂e evitados por km-pasajero (coche medio). */
  co2PerPassengerKm: 0.143,
  /** Parte de los activos que conduce (el resto va de pasajero). */
  driverShare: 0.4,
};

/** Mismo funnel en el periodo anterior: lo que da el «vs periodo ant.». */
const PREVIOUS_FUNNEL = [221, 140, 112, 61, 35];

/** Reparto de los trayectos por hora (6h…20h) y por día laborable: picos de ida y vuelta. */
const HOUR_WEIGHTS = [1, 5, 13, 9, 2, 1, 1, 2, 2, 1, 2, 4, 11, 9, 3];
export const SAMPLE_HOUR_LABELS = ['6h', '7h', '8h', '9h', '10h', '11h', '12h', '13h', '14h', '15h', '16h', '17h', '18h', '19h', '20h'];
const WEEKDAY_WEIGHTS = [24, 22, 21, 20, 13];

/** Quien más km ha conducido con pasajeros: empleados de `assets/demo/employees.json`. */
const TOP_CARPOOLERS = [
  { name: 'Lucía García Martín', officeId: 'demo-office-madrid', trips: 21, kmWithPassengers: 236, kmTotal: 262 },
  { name: 'Hugo Fernández López', officeId: 'demo-office-getafe', trips: 18, kmWithPassengers: 198, kmTotal: 225 },
  { name: 'Martina Sánchez Ruiz', officeId: 'demo-office-madrid', trips: 16, kmWithPassengers: 171, kmTotal: 196 },
  { name: 'Álvaro Domínguez Gil', officeId: 'demo-office-getafe', trips: 14, kmWithPassengers: 149, kmTotal: 170 },
  { name: 'Sofía Moreno Álvarez', officeId: 'demo-office-madrid', trips: 12, kmWithPassengers: 127, kmTotal: 151 },
  { name: 'Valeria Navarro Torres', officeId: 'demo-office-madrid', trips: 11, kmWithPassengers: 113, kmTotal: 138 },
  { name: 'Adrián Serrano Blanco', officeId: 'demo-office-getafe', trips: 10, kmWithPassengers: 104, kmTotal: 121 },
];

export interface SampleCarpooler {
  name: string;
  trips: number;
  kmWithPassengers: number;
  kmTotal: number;
}

export interface SampleMetrics {
  declared: number;
  registered: number;
  linked: number;
  /** % de la plantilla registrada (redondeado). */
  registeredPercent: number;
  /** Usuarios activos hoy, en los últimos 7 días y en el periodo pedido. */
  activeToday: number;
  activeWeek: number;
  activePeriod: number;
  drivers: number;
  passengers: number;
  sharedTrips: number;
  routines: number;
  kmWithPassengers: number;
  passengersPerTrip: number;
  co2AvoidedKg: number;
  /** Funnel: registrados, con rutina, viaje realizado, viaje compartido, recurrentes. */
  funnel: number[];
  funnelPrevious: number[];
  tripsByHour: number[];
  tripsByWeekday: number[];
  topCarpoolers: SampleCarpooler[];
}

export interface SampleQuery {
  /** Sede elegida; sin ella (o «todos los centros»), toda la empresa. */
  officeId?: string | null;
  /** Días del periodo (por defecto, 30). */
  days?: number;
}

/**
 * Parte de la empresa que pesa la sede: 1 para toda la empresa y para una sede
 * que no es del ejemplo (una empresa de verdad con «Ver con datos de ejemplo»,
 * que suele tener una sola).
 */
export function sampleOfficeShare(officeId?: string | null): number {
  return (officeId && OFFICE_SHARE[officeId]) || 1;
}

/** Cifras del ejemplo para una sede y un periodo. */
export function sampleMetrics({ officeId, days = 30 }: SampleQuery = {}): SampleMetrics {
  const share = sampleOfficeShare(officeId);
  const scaled = (n: number) => Math.round(n * share);
  const periodFactor = days / 30;

  const declared = scaled(SAMPLE_DECLARED_EMPLOYEES);
  const registered = scaled(BASE.registered);
  const linked = scaled(BASE.linked);
  const activePeriod = Math.min(linked, scaled(activeForDays(days)));
  const activeRatio = activeForDays(days) / activeForDays(30);
  const sharedTrips = Math.round(BASE.sharedTrips * share * periodFactor);
  const kmWithPassengers = roundTo(sharedTrips * BASE.kmPerTrip, 10);
  const drivers = Math.round(activePeriod * BASE.driverShare);

  // El funnel es de cohorte: los registrados no dependen del periodo, el resto
  // de etapas sí, y ninguna puede pasar de la anterior.
  const funnel = [BASE.registered, BASE.withRoutine, BASE.tripCompleted, BASE.sharedTrip, BASE.recurrent]
    .map((n, i) => (i === 0 ? scaled(n) : scaled(n * Math.min(activeRatio, 1.3))));
  for (let i = 1; i < funnel.length; i++) funnel[i] = Math.min(funnel[i], funnel[i - 1]);
  const funnelPrevious = PREVIOUS_FUNNEL.map((n, i) => (i === 0 ? scaled(n) : scaled(n * Math.min(activeRatio, 1.3))));

  const topCarpoolers = TOP_CARPOOLERS
    .filter((c) => share === 1 || c.officeId === officeId)
    .slice(0, 5)
    .map(({ name, trips, kmWithPassengers: km, kmTotal }) => ({
      name,
      trips: Math.max(1, Math.round(trips * periodFactor)),
      kmWithPassengers: Math.round(km * periodFactor),
      kmTotal: Math.round(kmTotal * periodFactor),
    }));

  return {
    declared,
    registered,
    linked,
    registeredPercent: Math.round((registered / declared) * 100),
    activeToday: scaled(activeForDays(1)),
    activeWeek: scaled(activeForDays(Math.min(days, 7))),
    activePeriod,
    drivers,
    passengers: activePeriod - drivers,
    sharedTrips,
    routines: scaled(BASE.withRoutine),
    kmWithPassengers,
    passengersPerTrip: BASE.passengersPerTrip,
    co2AvoidedKg: sampleCo2ForKm(kmWithPassengers),
    funnel,
    funnelPrevious,
    tripsByHour: distribute(sharedTrips, HOUR_WEIGHTS),
    tripsByWeekday: distribute(sharedTrips, WEEKDAY_WEIGHTS),
    topCarpoolers,
  };
}

/**
 * kg de CO₂e evitados por unos km compartidos: cada pasajero deja su coche en
 * casa (1,6 pasajeros por coche de media). Redondeado a 5 kg.
 */
export function sampleCo2ForKm(km: number): number {
  return roundTo(km * BASE.passengersPerTrip * BASE.co2PerPassengerKm, 5);
}

/** Rutinas de las últimas 24 h: las rutas de ejemplo del mapa de la sede (o de todas). */
export function sampleRoutinesLast24h(officeId?: string | null): number {
  if (!officeId || !OFFICE_SHARE[officeId]) return SAMPLE_ROUTES.length;
  return SAMPLE_ROUTES.filter((r) => r.officeId === officeId).length;
}

function activeForDays(days: number): number {
  if (days <= ACTIVE_BY_DAYS[0][0]) return ACTIVE_BY_DAYS[0][1];
  for (let i = 1; i < ACTIVE_BY_DAYS.length; i++) {
    const [d1, v1] = ACTIVE_BY_DAYS[i - 1];
    const [d2, v2] = ACTIVE_BY_DAYS[i];
    if (days <= d2) {
      // Interpolación en escala logarítmica de días.
      const t = (Math.log(days) - Math.log(d1)) / (Math.log(d2) - Math.log(d1));
      return v1 + (v2 - v1) * t;
    }
  }
  return ACTIVE_BY_DAYS[ACTIVE_BY_DAYS.length - 1][1];
}

/** Reparte `total` según los pesos, sin perder ni sumar de más por el redondeo. */
export function distribute(total: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  const exact = weights.map((w) => (total * w) / sum);
  const out = exact.map(Math.floor);
  let rest = total - out.reduce((a, b) => a + b, 0);
  exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac)
    .forEach(({ i }) => {
      if (rest > 0) {
        out[i]++;
        rest--;
      }
    });
  return out;
}

function roundTo(n: number, step: number): number {
  return Math.round(n / step) * step;
}
